import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../../constants';
import { fetchWrapper } from '../../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IDischargeCriteria, IDischargeTransitionReason } from '../../utils/types';

export interface DischargeTransitionState {
  treatmentPlanId: number | null;
  dischargeCriteria: IDischargeCriteria | Partial<IDischargeCriteria>;
  reasons: IDischargeTransitionReason[];
  error: any;
  dischargeErrorCount: any;
  reasonsErrorCount: any;
  dischargeLoading: boolean;
  reasonsLoading: boolean;
}

type DischargeTransitionStateUpdate = {
  [Key in keyof DischargeTransitionState]: { key: Key; data: DischargeTransitionState[Key] };
}[keyof DischargeTransitionState];

const name = 'dischargeTransition';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<DischargeTransitionState>) =>
    createExtraReducers(builder),
});

function createInitialState(): DischargeTransitionState {
  return {
    treatmentPlanId: null,
    dischargeCriteria: {},
    reasons: [],
    error: null,
    dischargeErrorCount: 0,
    reasonsErrorCount: 0,
    dischargeLoading: false,
    reasonsLoading: false,
  };
}

function createReducers() {
  return {
    updateState,
    resetState,
  };

  function updateState(
    state: DischargeTransitionState,
    action: PayloadAction<DischargeTransitionStateUpdate>,
  ) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }

  function resetState(state: DischargeTransitionState) {
    state.treatmentPlanId = null;
    state.dischargeCriteria = {};
    state.reasons = [];
    state.dischargeErrorCount = 0;
    state.reasonsErrorCount = 0;
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    getDischarge: getDischarge(),
    createDischarge: createDischarge(),
    updateDischarge: updateDischarge(),
    getReasons: getReasons(),
    createReason: createReason(),
    removeReason: removeReason(),
    updateReason: updateReason(),
  };

  function getDischarge() {
    return createAsyncThunk(
      `${name}/getDischarge`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${treatmentPlanId}/discharges/`,
          getAccessTokenSilently,
        });

        return response;
      },
    );
  }
  function createDischarge() {
    return createAsyncThunk(
      `${name}/createDischarge`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/discharges/`,
            getAccessTokenSilently,
          },
          data,
        );

        return response;
      },
    );
  }
  function updateDischarge() {
    return createAsyncThunk(
      `${name}/updateDischarge`,
      async ({ treatmentPlanId, dischargeId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/discharges/${dischargeId}/`,
            getAccessTokenSilently,
          },
          data,
        );

        return response;
      },
    );
  }
  function getReasons() {
    return createAsyncThunk(
      `${name}/getReasons`,
      async ({ treatmentPlanId, dischargeId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${treatmentPlanId}/discharges/${dischargeId}/reasons/`,
          getAccessTokenSilently,
        });

        return response;
      },
    );
  }
  function createReason() {
    return createAsyncThunk(
      `${name}/createReason`,
      async ({ treatmentPlanId, dischargeId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/discharges/${dischargeId}/reasons/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }
  function removeReason() {
    return createAsyncThunk(
      `${name}/removeReason`,
      async ({ treatmentPlanId, dischargeId, reasonId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/discharges/${dischargeId}/reasons/${reasonId}/`,
          getAccessTokenSilently,
        });

        return response;
      },
    );
  }
  function updateReason() {
    return createAsyncThunk(
      `${name}/updateReason`,
      async ({ treatmentPlanId, dischargeId, reasonId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/discharges/${dischargeId}/reasons/${reasonId}/`,
            getAccessTokenSilently,
          },
          data,
        );

        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<DischargeTransitionState>) {
  const {
    getDischarge,
    createDischarge,
    updateDischarge,
    getReasons,
    createReason,
    removeReason,
    updateReason,
  } = extraActions;
  builder
    // getDischarge()
    .addCase(getDischarge.pending, (state: DischargeTransitionState) => {
      state.dischargeLoading = true;
      state.error = null;
    })
    .addCase(
      getDischarge.fulfilled,
      (
        state: DischargeTransitionState,
        action: PayloadAction<IDischargeCriteria, string, { arg: { forceUpdate: boolean } }>,
      ) => {
        state.dischargeLoading = false;
        if (
          action.meta.arg.forceUpdate ||
          (action.payload &&
            Object.keys(action.payload).length !== 0 &&
            (!state.dischargeCriteria || Object.keys(state.dischargeCriteria).length === 0))
        ) {
          state.dischargeCriteria = action.payload;
        }
      },
    )
    .addCase(getDischarge.rejected, (state: DischargeTransitionState, action: any) => {
      state.error = action.error;
      state.dischargeLoading = false;
      toast.error(state.error.message);
    })
    // createDischarge()
    .addCase(createDischarge.pending, (state: DischargeTransitionState) => {
      state.dischargeLoading = true;
      state.error = null;
    })
    .addCase(
      createDischarge.fulfilled,
      (state: DischargeTransitionState, action: PayloadAction<IDischargeCriteria>) => {
        state.dischargeLoading = false;
        state.dischargeCriteria = action.payload;
      },
    )
    .addCase(createDischarge.rejected, (state: DischargeTransitionState, action: any) => {
      state.dischargeLoading = false;
      state.error = action.error;
      toast.error(state.error.message);
    })
    // updateDischarge()
    .addCase(updateDischarge.pending, (state: DischargeTransitionState) => {
      state.dischargeLoading = true;
      state.error = null;
    })
    .addCase(
      updateDischarge.fulfilled,
      (state: DischargeTransitionState, action: PayloadAction<IDischargeCriteria>) => {
        state.dischargeLoading = false;
        state.dischargeCriteria = action.payload;
      },
    )
    .addCase(updateDischarge.rejected, (state: DischargeTransitionState, action: any) => {
      state.dischargeLoading = false;
      state.error = action.error;
      toast.error(state.error.message);
    })
    // getReasons()
    .addCase(getReasons.pending, (state: DischargeTransitionState) => {
      state.reasonsLoading = true;
      state.error = null;
    })
    .addCase(
      getReasons.fulfilled,
      (state: DischargeTransitionState, action: PayloadAction<IDischargeTransitionReason[]>) => {
        state.reasonsLoading = false;
        state.reasons = action.payload;
      },
    )
    .addCase(getReasons.rejected, (state: DischargeTransitionState, action: any) => {
      state.reasonsLoading = false;
      state.error = action.error;
      toast.error(state.error.message);
    })
    // createReason()
    .addCase(createReason.pending, (state: DischargeTransitionState) => {
      state.reasonsLoading = true;
      state.error = null;
    })
    .addCase(
      createReason.fulfilled,
      (state: DischargeTransitionState, action: PayloadAction<IDischargeTransitionReason>) => {
        state.reasonsLoading = false;
        // mix this value into the existing state
        state.reasons = [...state.reasons, action.payload];
      },
    )
    .addCase(createReason.rejected, (state: DischargeTransitionState, action: any) => {
      state.reasonsLoading = false;
      state.error = action.error;
      toast.error(state.error.message);
    })
    // removeReason()
    .addCase(removeReason.pending, (state: DischargeTransitionState) => {
      state.reasonsLoading = true;
      state.error = null;
    })
    .addCase(
      removeReason.fulfilled,
      (
        state: DischargeTransitionState,
        action: PayloadAction<IDischargeTransitionReason, string, { arg: { reasonId: number } }>,
      ) => {
        state.reasonsLoading = false;
        state.reasons = state.reasons.filter(
          (transitionReason) => transitionReason.id !== action.meta.arg.reasonId,
        );
      },
    )
    .addCase(removeReason.rejected, (state: DischargeTransitionState, action: any) => {
      state.reasonsLoading = false;
      state.error = action.error;
      toast.error(state.error.message);
    })
    // updateReasons()
    .addCase(updateReason.pending, (state: DischargeTransitionState) => {
      state.reasonsLoading = true;
      state.error = null;
    })
    .addCase(
      updateReason.fulfilled,
      (state: DischargeTransitionState, action: PayloadAction<IDischargeTransitionReason>) => {
        state.reasonsLoading = false;
        state.reasons = [...state.reasons, action.payload];
      },
    )
    .addCase(updateReason.rejected, (state: DischargeTransitionState, action: any) => {
      state.reasonsLoading = false;
      state.error = action.error;
      toast.error(state.error.message);
    });
}

export const dischargeTransitionActions = { ...slice.actions, ...extraActions };
export const dischargeTransitionReducer = slice.reducer;
